import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from '@emotion/styled';
import { Layout } from '../components/Layout';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  margin-top: 80px;
`

const Title = styled.h1`
  color: #4CAF50;
  margin-bottom: 2rem;
`

const Section = styled.section`
  margin-bottom: 2rem;
  
  h2 {
    color: #2c5282;
    margin-bottom: 1rem;
  }
  
  p {
    line-height: 1.6;
    color: #4a5568;
  }
  
  ul {
    list-style-type: none;
    padding: 0;
    
    li {
      padding: 0.5rem 0;
      color: #4a5568;
      display: flex;
      align-items: center;
      
      &:before {
        content: '✓';
        color: #4CAF50;
        margin-right: 0.5rem;
      }
    }
  }
`

const ImageContainer = styled.div`
  margin: 2rem 0;
  img {
    width: 100%;
    max-width: 600px;
    height: auto;
    border-radius: 8px;
    margin: 0 auto;
    display: block;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }
`

function About() {
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'mainEntity': {
      '@type': 'Organization',
      'name': 'Farmers Market Finder',
      'description': 'Connecting communities with local farmers markets for fresh, sustainable produce.',
      'url': 'https://farmersmarketfinder.com',
      'sameAs': [
        'https://facebook.com/farmersmarketfinder',
        'https://twitter.com/farmersmktfind',
        'https://instagram.com/farmersmarketfinder'
      ]
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>About Farmers Market Finder - Supporting Local Agriculture</title>
        <meta name="description" content="Learn about Farmers Market Finder's mission to connect communities with local farmers markets, promoting sustainable agriculture and healthy food choices." />
        <meta property="og:title" content="About Farmers Market Finder - Supporting Local Agriculture" />
        <meta property="og:description" content="Learn about Farmers Market Finder's mission to connect communities with local farmers markets, promoting sustainable agriculture and healthy food choices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://farmersmarketfinder.com/about" />
        <meta property="og:image" content="https://farmersmarketfinder.com/about-hero.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Farmers Market Finder - Supporting Local Agriculture" />
        <meta name="twitter:description" content="Learn about Farmers Market Finder's mission to connect communities with local farmers markets, promoting sustainable agriculture and healthy food choices." />
        <meta name="twitter:image" content="https://farmersmarketfinder.com/about-hero.jpg" />
        
        <meta name="keywords" content="farmers markets, local agriculture, sustainable farming, community markets, fresh produce, organic food" />
        <link rel="canonical" href="https://farmersmarketfinder.com/about" />
        
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <Container>
        <Title>About Farmers Market Finder</Title>
        
        <Section>
          <h2>Our Mission</h2>
          <p>
            At Farmers Market Finder, we're dedicated to connecting communities with local farmers markets, 
            making it easier for people to access fresh, sustainable, and locally-grown produce. Our platform 
            helps bridge the gap between local farmers and consumers, promoting healthy eating habits and 
            supporting local agriculture.
          </p>
        </Section>

        <ImageContainer>
          <img 
            src="/images/farmers-market.jpg" 
            alt="Fresh produce at a local farmers market"
            loading="lazy"
          />
        </ImageContainer>

        <Section>
          <h2>Why Choose Local Markets?</h2>
          <ul>
            <li>Support local farmers and businesses</li>
            <li>Access to fresh, seasonal produce</li>
            <li>Reduce environmental impact</li>
            <li>Connect with your community</li>
            <li>Learn about sustainable farming practices</li>
          </ul>
        </Section>

        <Section>
          <h2>Our Impact</h2>
          <p>
            Since our launch, we've helped thousands of people discover local farmers markets in their area. 
            Our platform has connected countless consumers with local farmers, contributing to the growth of 
            sustainable agriculture and stronger local food systems.
          </p>
        </Section>

        <Section>
          <h2>Join the Movement</h2>
          <p>
            Whether you're a passionate food lover, health enthusiast, or someone who cares about supporting 
            local communities, Farmers Market Finder is here to help you discover the best local markets in 
            your area. Join us in supporting local agriculture and sustainable food systems.
          </p>
        </Section>
      </Container>
    </Layout>
  );
}

export default About;
